import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { TaskHelpers } from 'ontraccr-common';

import { ReportColumn } from './ReportColumnHeader';
import ReportSkeleton from './ReportSkeleton';
import getEmployeeProjectFilters from './EmployeeProjectFilters';

import { getIdMap } from '../../helpers/helpers';
import { msToHours } from '../../helpers/time';

import {
  filterOnCols,
  sortAlphabetically,
  toFixed,
  getEstimatedHours,
  prepareReportData,
  getDateKey,
  isValidTaskHelper,
  failsFilter,
  failsCostcodeFilter,
  failsProjectFilter,
  getInitialFilters,
  getUnphasedHoursPerProject,
  getCostcodeInitialOptions,
  getReferencedCostcodeIds,
  COSTCODE_BY_IDS_COLUMNS,
} from '../reportHelpers';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import {
  getCostcodeCategoryAssignmentData,
  searchCostcodeOptions,
  getCostcodeByIds,
} from '../../costcodes/state/costcodes.actions';

const allColumns = [
  ReportColumn('Project', {
    fixed: 'left',
  }),
  ReportColumn('Phase'),
  ReportColumn('Cost Code', {
    key: 'costcode',
    dataIndex: 'costcode',
  }),
  ReportColumn('Est. Hrs', {
    key: 'estimatedHours',
    dataIndex: 'estimatedHours',
  }),
  ReportColumn('Act. Hrs', {
    key: 'actualHours',
    dataIndex: 'actualHours',
  }),
  ReportColumn('Rem. Hrs', {
    key: 'remainingHours',
    dataIndex: 'remainingHours',
  }),
  ReportColumn('% Hrs Used', {
    key: 'hoursUsed',
    dataIndex: 'hoursUsed',
  }),
];

const dateFormat = 'MMM D';

class ProjectStatus extends React.Component {
  constructor(props) {
    super(props);
    const {
      height = 450,
      costcodes = [],
      projects = [],
      phases = [],
      allProjects = [],
      allPhases = [],
      selectAllProjects = false,
      selectAllPhases = false,
      selectAllCostcodes = false,
      hasNullProject,
      hasNullPhase,
      hasNullCostcode,
      onColumnsChanged,
      columns,
      settings = {},
    } = this.props;

    this.checkedColumns = new Set(allColumns.map((col) => col.key).filter(filterOnCols(columns)));
    this.state = {
      columns: allColumns.filter((col) => this.checkedColumns.has(col.key)),
      height,
      enabledFilters: {
        project: true,
        costcode: true,
        phase: true,
      },
      ...getInitialFilters({
        addUsers: false,
        selectAllProjects,
        allProjects,
        projects,
        selectAllCostcodes,
        costcodes,
        phases,
        selectAllPhases,
        allPhases,
        hasNullProject,
        hasNullPhase,
        hasNullCostcode,
        settings,
      }),
    };

    onColumnsChanged(this.state.columns);
    this.onResize = this.resize.bind(this);
    this.onFilterToggle = this.toggleFilter.bind(this);

    this.projectIdMap = getIdMap(allProjects);
  }

  componentDidMount() {
    const {
      onRef,
      getCostcodeCategoryAssignmentData,
    } = this.props;
    onRef(this);
    this.resize();
    this.fetchCategoryAssignmentData(getCostcodeCategoryAssignmentData);
    this.fetchReferencedCostcodes();
  }

  componentDidUpdate(prevProps) {
    const {
      height: prevHeight,
      projects: prevProjects,
      allPhases: prevAllPhases,
      timeEntryUserMap: prevTimeEntryUserMap,
    } = prevProps;
    const {
      height,
      projects,
      allPhases,
      allProjects,
      timeEntryUserMap,
      getCostcodeCategoryAssignmentData,
    } = this.props;
    if (height !== prevHeight) {
      this.setState({
        height,
      });
    }

    if (allProjects !== prevProps.allProjects) {
      this.projectIdMap = getIdMap(allProjects);
    }

    const prevIds = new Set((prevProjects ?? []).map(({ id }) => id));
    const currIds = new Set((projects ?? []).map(({ id }) => id));
    const projectIdsChanged = prevIds.size !== currIds.size
      || [...currIds].some((id) => !prevIds.has(id));
    if (currIds.size > 0 && projectIdsChanged) {
      this.fetchCategoryAssignmentData(getCostcodeCategoryAssignmentData);
    }

    if (timeEntryUserMap !== prevTimeEntryUserMap || allPhases !== prevAllPhases) {
      this.fetchReferencedCostcodes();
    }
  }

  componentWillUnmount() {
    const {
      onRef,
    } = this.props;
    onRef(null);
  }

  fetchCategoryAssignmentData(getCostcodeCategoryAssignmentData) {
    const { projects = [] } = this.props;
    const projectIds = projects.map(({ id }) => id);
    if (projectIds.length > 0) {
      getCostcodeCategoryAssignmentData({ projectIds });
    }
  }

  // Cost codes are loaded on demand: the report references those used by time entries plus
  // any that phases point at (so phased estimated-hours rows still render).
  fetchReferencedCostcodes() {
    const {
      timeEntryUserMap = {},
      allPhases = [],
      getCostcodeByIds: fetchByIds,
    } = this.props;
    const ids = new Set(getReferencedCostcodeIds(timeEntryUserMap));
    allPhases.forEach(({ costcodeId }) => {
      if (costcodeId) ids.add(costcodeId);
    });
    if (ids.size) {
      fetchByIds(Array.from(ids), { columns: COSTCODE_BY_IDS_COLUMNS.PROJECT_STATUS });
    }
  }

  prepareData() {
    const {
      costcodes = [],
      projects = [],
      phases = [],
      allPhases = [],
      allProjects = [],
      costcodeIdMap = {},
      categoryAssignments = {},
    } = this.props;
    const referencedCostcodes = Object.values(costcodeIdMap).map((cc) => ({ ...cc }));
    const prepared = prepareReportData({
      costcodes,
      projects,
      phases,
      allCostcodes: referencedCostcodes,
      allPhases,
      allProjects,
    });
    return {
      ...prepared,
      unphasedHours: getUnphasedHoursPerProject({
        allPhases,
        phases,
        costcodes: referencedCostcodes,
        projects,
        allProjects,
        categoryAssignments,
      }),
    };
  }

  getDates() {
    const {
      dateRange = [moment(), moment()],
    } = this.props;

    const startDate = moment.isMoment(dateRange[0])
      ? dateRange[0].clone() : moment();
    const endDate = dateRange[1];
    const dates = [];
    while (startDate.isBefore(endDate)) {
      dates.push(startDate.format(dateFormat));
      startDate.add(1, 'd');
    }
    return dates;
  }

  getColFilters() {
    return [{
      title: 'Estimated Hrs',
      key: 'estimatedHours',
      checked: this.checkedColumns.has('estimatedHours'),
    }, {
      title: 'Actual Hrs',
      key: 'actualHours',
      checked: this.checkedColumns.has('actualHours'),
    }, {
      title: 'Remaining Hrs',
      key: 'remainingHours',
      checked: this.checkedColumns.has('remainingHours'),
    }, {
      title: '% Hrs Used',
      key: 'hoursUsed',
      checked: this.checkedColumns.has('hoursUsed'),
    }];
  }

  getRowFilters() {
    const {
      allProjects = [],
      allPhases = [],
      settings = {},
      t,
      searchCostcodeOptions: searchCostcodeOptionsAction,
      costcodeIdMap = {},
    } = this.props;
    const {
      projects: selectedProjects,
      costcodes: selectedCostcodes,
      phases: selectedPhases,
    } = this.state;

    const relevantPhases = selectedProjects.size === 0
      ? allPhases : allPhases.filter((phase) => {
        const project = this.projectIdMap[phase.projectId];
        return project && selectedProjects.has(
          formatProjectLabelFromCompanySettings({
            name: project?.name,
            number: project?.number,
            settings,
          }),
        );
      });

    return getEmployeeProjectFilters({
      onFilterToggle: (type, checked) => {
        this.setState({
          [type]: checked,
        });
      },
      onCheckChanged: (type, checked) => {
        this.updateRowFilters({
          [type]: checked,
          [`${type}Description`]: checked,
        });
      },
      projects: allProjects,
      phases: relevantPhases,
      showNoneCostcodeOption: false,
      showGlobalCostcodes: true,
      costcodeFetchOptions: searchCostcodeOptionsAction,
      costcodeInitialOptions: getCostcodeInitialOptions(
        Object.values(costcodeIdMap),
        { includeNone: false },
      ),
      costcodeProjects: allProjects,
      costcodePhases: allPhases,
      showEmployee: false,
      projectCheckDisabled: true,
      checkedColumns: this.checkedColumns,
      settings,
      t,
      filters: {
        projects: selectedProjects,
        costcodes: selectedCostcodes,
        phases: selectedPhases,
      },
    });
  }

  getAggregateKey(projectName, phaseName, costcodeName) {
    const {
      enabledFilters: {
        costcode,
        project,
        phase,
      },
    } = this.state;

    const keys = [projectName, phaseName, costcodeName];
    const filters = [costcode, phase, project];
    const filterNames = ['costcode', 'phase', 'project'];
    for (const index in filters) {
      const filter = filters[index];
      if (filter) return { key: keys.join('-'), lowestFilter: filterNames[index] };
      keys.pop();
    }
    return { key: 'All', lowestFilter: 'All' };
  }

  updateRowFilters(newFilters) {
    this.setState({
      enabledFilters: {
        ...this.state.enabledFilters,
        ...newFilters,
      },
    });
    for (const key in newFilters) {
      this.toggleFilter({ key }, newFilters[key]);
    }
  }

  toggleFilter(item, checked) {
    const {
      key: itemKey,
    } = item;

    if (checked) {
      this.checkedColumns.add(itemKey);
    } else {
      this.checkedColumns.delete(itemKey);
    }
    this.setState({
      columns: allColumns.filter((col) => this.checkedColumns.has(col.key)),
    });
  }

  resize() {
    const {
      height,
    } = this.props;
    this.setState({
      height,
    });
  }

  formatData() {
    const {
      dateRange,
      allUsers = [],
      onColumnsChanged,
      onDataChanged,
      onFiltersChanged,
      timeEntryUserMap = {},
      settings = {},
      categoryAssignments = {},
    } = this.props;

    const {
      projects,
      costcodes,
      phases,
      columns,
    } = this.state;

    const {
      costcodeIdMap,
      phaseMap,
      unphasedHours,
      costcodePhaseList,
    } = this.prepareData();

    const [startDate] = dateRange;
    const data = [];
    const aggregator = {};

    allUsers.forEach((user) => {
      const tasks = timeEntryUserMap[user?.id] ?? [];
      tasks.forEach((task) => {
        const isValidTask = isValidTaskHelper({
          filters: {
            projects,
            costcodes,
            phases,
          },
          dateRange,
          projectIdMap: this.projectIdMap,
          costcodeIdMap,
          phaseMap,
          checkPhase: true,
          settings,
        })(task);

        if (!isValidTask) return;

        const project = this.projectIdMap[task.projectId];
        const phase = phaseMap[task.phaseId];
        const costcode = costcodeIdMap[task.costcodeId];
        if (!costcode) return;
        const {
          name,
          number,
        } = project || {};

        const projectLabel = formatProjectLabelFromCompanySettings({ name, number, settings });

        const costCode = costcode ? costcode.code : 'None';
        const projectName = project ? projectLabel : 'None';
        const phaseName = phase ? phase.name : 'None';
        const { key: aggKey, lowestFilter } = (
          this.getAggregateKey(projectName, phaseName, costCode)
        );

        if (!(aggKey in aggregator)) {
          aggregator[aggKey] = {
            project: project ? projectLabel : null,
            costcode: costcode ? `${costcode.code} - ${costcode.name}` : null,
            estimatedHours: getEstimatedHours({
              lowestFilter,
              project,
              phase,
              costcode,
              unphasedHours,
              categoryAssignments,
              phaseId: task.phaseId,
            }),
            actualHours: 0,
            phase: phase ? phase.name : null,
            completed: [0.0],
            startDate,
            progressGraph: {},
            key: aggKey,
          };
        }
        const ourAgg = aggregator[aggKey];
        const {
          regularTime,
          overtime,
          doubleOT,
        } = TaskHelpers.getRuntimes(task, false);
        const runTime = regularTime + overtime + doubleOT;
        const endDateKey = getDateKey(task, dateFormat);
        const totalHours = msToHours(runTime);
        ourAgg.actualHours += totalHours;
        if (ourAgg.estimatedHours > 0) {
          const prevValue = ourAgg.progressGraph[endDateKey] || 0;
          ourAgg.progressGraph[endDateKey] = prevValue
            + (ourAgg.actualHours / ourAgg.estimatedHours);
        }
      });
    });

    costcodePhaseList.forEach(({ costcodeId, phaseId }) => {
      const costcode = costcodeIdMap[costcodeId];
      if (!costcode?.projectId) return;
      const project = this.projectIdMap[costcode.projectId];
      if (failsProjectFilter(projects, project, settings)) return;

      if (failsCostcodeFilter(costcodes, costcode)) return;
      if (!costcode.canClockIn) return;

      const phase = phaseMap[phaseId];
      if (failsFilter(phases, phase)) return;

      const {
        name,
        number,
      } = project || {};

      const projectLabel = formatProjectLabelFromCompanySettings({ name, number, settings });

      const costCode = costcode ? costcode.code : 'None';
      const projectName = project ? projectLabel : 'None';
      const phaseName = phase ? phase.name : 'None';

      const { key: aggKey, lowestFilter } = this.getAggregateKey(projectName, phaseName, costCode);

      if (!(aggKey in aggregator)) {
        aggregator[aggKey] = {
          project: project ? projectLabel : null,
          costcode: costcode ? `${costcode.code} - ${costcode.name}` : null,
          estimatedHours: getEstimatedHours({
            lowestFilter,
            project,
            phase,
            costcode,
            unphasedHours,
            categoryAssignments,
            phaseId,
          }),
          actualHours: 0,
          phase: phase ? phase.name : null,
          completed: [0.0],
          startDate,
          progressGraph: {},
          key: aggKey,
        };
      }
    });

    // TODO: REFACTOR
    Object.keys(aggregator).forEach((key) => {
      const hours = aggregator[key];
      data.push({
        ...hours,
        estimatedHours: toFixed(hours.estimatedHours, false),
        actualHours: toFixed(hours.actualHours, false),
        remainingHours: toFixed(Math.max(0, hours.estimatedHours - hours.actualHours), false),
        hoursUsed: hours.estimatedHours && hours.estimatedHours !== 0
          ? `${toFixed(100 * (hours.actualHours / hours.estimatedHours), false)}%` : null,
      });
    });
    const res = [...data].sort(sortAlphabetically());
    onColumnsChanged(columns);
    onDataChanged(res);
    onFiltersChanged({
      users: allUsers,
      projects,
      costcodes,
      phases,
    });
    return res;
  }

  render() {
    const {
      renderColFilters,
      renderRowFilters,
    } = this.props;
    const {
      columns = [],
      height,
    } = this.state;
    const data = this.formatData();

    return (
      <ReportSkeleton
        data={data}
        columns={columns}
        height={height}
        colFilters={renderColFilters(this.getColFilters())}
        rowFilters={renderRowFilters(this.getRowFilters())}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  categoryAssignments: state.costcodes.categoryAssignments,
  costcodeIdMap: state.costcodes.costcodeIdMap,
});

export default withTranslation()(connect(mapStateToProps, {
  getCostcodeCategoryAssignmentData,
  searchCostcodeOptions,
  getCostcodeByIds,
})(ProjectStatus));
